import { useState } from 'react';
import { Link } from 'react-router';
import { useStore } from '../store/useStore';
import { trpc } from '../trpc/client';
import { Lightbulb, Send, CheckCircle, Building2, Tag, DollarSign, MapPin, FileText, Plus, Trash2 } from 'lucide-react';

export default function RecommendDeal() {
  const { user } = useStore();
  const { data: categoriesData } = trpc.util.getCategories.useQuery({});
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    categoryId: '',
    dealName: '',
    dealPrice: '',
    currency: 'USD',
    supplierName: '',
    supplierAddress: '',
    supplierCountry: '',
    supplierCity: '',
    supplierPhone: '',
    description: '',
  });

  const categories = categoriesData || [];

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 mx-auto mb-6 bg-emerald-50 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-semibold text-[#1e293b] mb-2 font-display">Deal Recommended!</h2>
          <p className="text-[#64748b] mb-2">
            Your deal recommendation has been submitted with serial number <span className="font-mono font-semibold text-[#c2410c]">IPO-R-2025-000003</span>.
          </p>
          <p className="text-sm text-[#64748b] mb-6">
            An IPME will be assigned to verify this supplier. If the deal is approved and listed, you'll earn recommendation commissions on every sale.
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/" className="px-6 py-3 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold">
              Back to Home
            </Link>
            <button onClick={() => setSubmitted(false)} className="px-6 py-3 border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">
              Recommend Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-16 bg-[#f8fafc]">
      <div className="max-w-[800px] mx-auto px-4 lg:px-8">
        <div className="py-6">
          <h1 className="text-3xl font-semibold text-[#1e293b] font-display">Recommend a Deal</h1>
          <p className="text-[#64748b]">Know a great supplier? Recommend their deal and earn commissions.</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
          <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-[#312e81]/5 to-[#c2410c]/5 rounded-xl mb-6">
            <Lightbulb className="w-5 h-5 text-[#c2410c] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#1e293b]">Earn 10% of the IPO margin on recommended deals</p>
              <p className="text-xs text-[#64748b] mt-0.5">
                When a deal you recommend gets approved and purchased, you earn 10% of the margin. Plus 5% for your direct sponsor and 0.45% for levels 2-10.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            {/* Deal Info */}
            <div>
              <h3 className="text-sm font-semibold text-[#1e293b] uppercase tracking-wider mb-3">Deal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Deal Name *</label>
                  <input type="text" value={form.dealName} onChange={(e) => handleChange('dealName', e.target.value)} placeholder="e.g., Industrial Solar Panel Installation" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Category</label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                    <select value={form.categoryId} onChange={(e) => handleChange('categoryId', e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] outline-none text-sm">
                      <option value="">Select category</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Price (Optional)</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                      <input type="number" value={form.dealPrice} onChange={(e) => handleChange('dealPrice', e.target.value)} placeholder="50000" className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Currency</label>
                    <select value={form.currency} onChange={(e) => handleChange('currency', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] outline-none text-sm">
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="AED">AED</option>
                      <option value="SGD">SGD</option>
                    </select>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Description</label>
                  <textarea value={form.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Describe the product or service..." rows={3} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm resize-none" />
                </div>
              </div>
            </div>

            {/* Supplier Info */}
            <div className="border-t border-gray-100 pt-5">
              <h3 className="text-sm font-semibold text-[#1e293b] uppercase tracking-wider mb-3">Supplier Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Supplier Name *</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                    <input type="text" value={form.supplierName} onChange={(e) => handleChange('supplierName', e.target.value)} placeholder="Company or individual name" className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Country</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                    <input type="text" value={form.supplierCountry} onChange={(e) => handleChange('supplierCountry', e.target.value)} placeholder="e.g., Ghana" className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">City</label>
                  <input type="text" value={form.supplierCity} onChange={(e) => handleChange('supplierCity', e.target.value)} placeholder="e.g., Accra" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Address</label>
                  <input type="text" value={form.supplierAddress} onChange={(e) => handleChange('supplierAddress', e.target.value)} placeholder="Full address" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Phone</label>
                  <input type="tel" value={form.supplierPhone} onChange={(e) => handleChange('supplierPhone', e.target.value)} placeholder="+233 123 456 789" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={handleSubmit}
                className="flex items-center gap-2 px-6 py-3 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold"
              >
                <Send className="w-4 h-4" /> Submit Recommendation
              </button>
              <button className="flex items-center gap-2 px-6 py-3 border border-gray-200 rounded-xl text-sm font-medium text-[#64748b] hover:bg-gray-50 transition-colors">
                <Trash2 className="w-4 h-4" /> Clear Form
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
